import { convertOrientationToRadians, convertAngleToRadians } from '../angle-utils.js';
import { colorStopToString, fixTransparentColorStops } from '../color-utils.js';

/**
 * Calculate CSS gradient line length
 * Per CSS Images Module Level 3, the gradient line length is:
 * abs(W * sin(θ)) + abs(H * cos(θ))
 * where θ is the gradient angle measured from vertical (0deg = to top)
 * @param {number} width - Rectangle width
 * @param {number} height - Rectangle height
 * @param {number} angleRad - Gradient angle in radians (CSS convention: 0 = to top)
 * @returns {number} Gradient line length
 */
function calculateGradientLineLength(width, height, angleRad) {
  return Math.abs(width * Math.sin(angleRad)) + Math.abs(height * Math.cos(angleRad));
}

/**
 * Calculate gradient line start and end points
 * @param {number} width - Rectangle width
 * @param {number} height - Rectangle height
 * @param {number} angleRad - Gradient angle in radians (CSS convention: 0 = to top)
 * @returns {Object} { start: {x, y}, end: {x, y} }
 */
function calculateGradientLinePoints(width, height, angleRad) {
  const cx = width / 2;
  const cy = height / 2;
  const lineLength = calculateGradientLineLength(width, height, angleRad);
  const halfLength = lineLength / 2;

  // CSS gradient angle: 0deg = to top, 90deg = to right
  // Convert to standard math angle: 0 = right, counter-clockwise positive
  // CSS 0deg (to top) = math 90deg (π/2)
  // CSS 90deg (to right) = math 0deg (0)
  const mathAngle = Math.PI / 2 - angleRad;

  const dx = Math.cos(mathAngle) * halfLength;
  const dy = -Math.sin(mathAngle) * halfLength; // Negative because canvas Y is inverted

  return {
    start: { x: cx - dx, y: cy + dy },
    end: { x: cx + dx, y: cy - dy },
  };
}

/**
 * Render a repeating linear gradient
 * @param {CanvasRenderingContext2D} context - Canvas context
 * @param {Object} gradientDef - Gradient definition from parser
 * @param {Object} bounds - Bounding box { x, y, w, h }
 * @returns {CanvasGradient} Canvas gradient object
 */
export function renderRepeatingLinearGradient(context, gradientDef, { x, y, w, h }) {
  // Get gradient angle in radians (CSS convention: 0 = to top)
  let angleRad;
  if (gradientDef.orientation?.value) {
    angleRad = convertOrientationToRadians(gradientDef.orientation);
  } else if (gradientDef.orientation) {
    angleRad = convertAngleToRadians(gradientDef.orientation);
  } else {
    angleRad = Math.PI; // Default: to bottom (180deg)
  }

  // Calculate gradient line points using CSS spec formula
  const { start, end } = calculateGradientLinePoints(w, h, angleRad);

  const gradient = context.createLinearGradient(x + start.x, y + start.y, x + end.x, y + end.y);

  // Clone colorStops to avoid mutating the original
  const colorStops = gradientDef.colorStops.map((stop) => ({ ...stop }));

  // Fix transparent color stops to match CSS premultiplied alpha behavior
  fixTransparentColorStops(colorStops);

  // Calculate the total gradient line distance
  const distance = calculateGradientLineLength(w, h, angleRad);

  // Calculate pattern length from color stops
  // For repeating gradients, we need to find the pattern length in pixels
  // Priority: use px values if available, otherwise use % values
  let patternLengthPx = 0;
  let patternLengthPercent = 0;
  let hasPxValues = false;

  colorStops.forEach((stop) => {
    if (stop.length) {
      if (stop.length.type === 'px') {
        hasPxValues = true;
        patternLengthPx = Math.max(patternLengthPx, parseFloat(stop.length.value));
      } else if (stop.length.type === '%') {
        patternLengthPercent = Math.max(patternLengthPercent, parseFloat(stop.length.value));
      }
    }
  });

  // Determine the actual pattern length in pixels
  let patternLength;
  if (hasPxValues && patternLengthPx > 0) {
    patternLength = patternLengthPx;
  } else if (patternLengthPercent > 0) {
    patternLength = (patternLengthPercent / 100) * distance;
  } else {
    // No explicit length found, use the full distance (no repeating)
    patternLength = distance;
  }

  // Avoid division by zero or infinite loops
  if (patternLength <= 0) {
    patternLength = distance;
  }

  // Calculate number of repetitions needed to cover the full gradient line
  const repetitions = Math.ceil(distance / patternLength);

  // Add repeated color stops
  for (let i = 0; i < repetitions; i++) {
    const patternOffsetPx = i * patternLength;

    colorStops.forEach((stop, index) => {
      let stopPositionPx = 0;

      if (stop.length) {
        if (stop.length.type === 'px') {
          stopPositionPx = parseFloat(stop.length.value);
        } else if (stop.length.type === '%') {
          // For repeating gradients with px-based pattern length,
          // % values are relative to the pattern length
          if (hasPxValues) {
            stopPositionPx = (parseFloat(stop.length.value) / 100) * patternLength;
          } else {
            // Pure percentage-based repeating gradient
            stopPositionPx = (parseFloat(stop.length.value) / 100) * distance;
          }
        }
      } else {
        // No length specified, distribute evenly within the pattern
        stopPositionPx = (index / (colorStops.length - 1)) * patternLength;
      }

      const position = (patternOffsetPx + stopPositionPx) / distance;
      const colorValue = colorStopToString(stop);

      if (colorValue && position >= 0 && position <= 1) {
        gradient.addColorStop(Math.min(Math.max(0, position), 1), colorValue);
      }
    });
  }

  return gradient;
}
